/**
 * Phase 6 Tier 1 (Feb 2026) — Global axios silent-refresh interceptor.
 *
 * Bug context: SA / dispatchers reported being logged out when returning to
 * the PWA after the 1-hour access-token TTL elapsed. Drivers rarely saw it
 * because their 20s heartbeat kept making authenticated requests inside the
 * 1h window. Root cause: every API call that hit a 401 silently failed, and
 * `AuthContext.checkAuth()` did not try `/auth/refresh` on cold-start before
 * bouncing to /login.
 *
 * What this interceptor does:
 *   - Skips requests to `/api/auth/refresh` and `/api/auth/login` (no loops).
 *   - On any other 401 response, transparently POSTs `/api/auth/refresh`,
 *     then re-issues the original request once.
 *   - Coalesces concurrent 401s into a single in-flight refresh — multiple
 *     simultaneous requests don't fire multiple refreshes.
 *   - If refresh itself fails, calls the `onUnauthorized` callback (which
 *     AuthContext wires to `setUser(false)`) and the original request error
 *     bubbles up for the caller to handle.
 *
 * Phase 2 (Feb 2026) — Dual-transport auth. Two new behaviors:
 *   1. REQUEST interceptor attaches `Authorization: Bearer <session_token>`
 *      on every same-origin API call when a valid localStorage token
 *      exists. Backend prefers the HttpOnly access cookie when present, so
 *      this is a no-op when cookies are alive and a silent lifeline when
 *      they've been evicted.
 *   2. RESPONSE interceptor on 401 forwards the localStorage
 *      `refresh_public` in the refresh body so the server can rehydrate
 *      the session even without any auth cookies. Also persists the
 *      rotated dual-transport pair returned by the refresh endpoint.
 *
 * Install once at app boot inside AuthProvider.
 */
import axios from 'axios';
import { currentBearer, currentPublicRefresh, saveSession } from './sessionStorage';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

let refreshInFlight = null;

// Feb 2026 (Phase A) — endpoints that must NEVER trigger a logout state
// change on a 401. Driver-side background pings (location PATCH, cart
// heartbeat, session heartbeat) can race the silent refresh mid-transition;
// if the refresh fails for a beat we absolutely do NOT want to boot the
// driver out of the app in the middle of a delivery. The next successful
// call will still refresh cleanly. Match by suffix of the URL path.
const BACKGROUND_TOLERANT_ENDPOINTS = [
  '/orders/', // any /api/orders/{id}/location or /status PATCH
  '/api/driver/heartbeat',
  '/api/driver/location',
  '/api/carts/heartbeat',
  '/api/auth/session',
];

function isBackgroundTolerant(url) {
  if (!url) return false;
  return BACKGROUND_TOLERANT_ENDPOINTS.some((p) => url.includes(p));
}

// Phase 2 (Feb 2026) — Attach Authorization: Bearer to same-origin API
// requests when a localStorage session token is present. Backend
// `get_current_user` prefers the access cookie so this header is only
// consulted when cookies have been evicted. We deliberately skip
// non-API URLs (e.g. Mapbox) and any request that already carries an
// Authorization header (unlikely, but respects explicit callers).
function attachBearerHeader(config) {
  try {
    const url = config?.url || '';
    if (!url.startsWith(API_URL) && !url.startsWith('/api')) return config;
    const headers = config.headers || {};
    if (headers.Authorization || headers.authorization) return config;
    const bearer = currentBearer();
    if (!bearer) return config;
    config.headers = { ...headers, Authorization: `Bearer ${bearer}` };
  } catch (_) {
    // Non-fatal — falling back to cookies is always safe.
  }
  return config;
}

export function installAuthInterceptor({ onUnauthorized }) {
  // Make sure we don't double-install in dev hot-reload
  if (axios.__ashMerchAuthInterceptor) {
    axios.interceptors.response.eject(axios.__ashMerchAuthInterceptor);
  }
  if (axios.__ashMerchAuthRequestInterceptor != null) {
    axios.interceptors.request.eject(axios.__ashMerchAuthRequestInterceptor);
  }
  const requestId = axios.interceptors.request.use(attachBearerHeader);
  axios.__ashMerchAuthRequestInterceptor = requestId;

  const id = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;
      if (status !== 401 || !original) return Promise.reject(error);
      // Don't try to refresh the refresh endpoint itself, login, or auth/me on
      // first ever check (handled separately in AuthContext.checkAuth).
      const url = original.url || '';
      if (url.includes('/api/auth/refresh') || url.includes('/api/auth/login') || url.includes('/api/auth/register') || url.includes('/api/auth/logout')) {
        return Promise.reject(error);
      }
      // Prevent infinite retry loops — only refresh+retry once per original req
      if (original._authRetry) return Promise.reject(error);
      original._authRetry = true;

      try {
        if (!refreshInFlight) {
          // Phase 2 (Feb 2026) — Attach localStorage refresh_public in
          // the body so the refresh can succeed even if both auth
          // cookies have been evicted by iOS.
          const publicRefresh = currentPublicRefresh();
          const refreshBody = publicRefresh ? { refresh_public: publicRefresh } : {};
          refreshInFlight = axios.post(
            `${API_URL}/api/auth/refresh`,
            refreshBody,
            { withCredentials: true },
          ).then((resp) => {
            // Persist the rotated dual-transport pair the server just issued.
            saveSession(resp?.data || {});
            return resp;
          }).finally(() => { refreshInFlight = null; });
        }
        await refreshInFlight;
      } catch (_refreshErr) {
        // Feb 2026 (Phase A) — for background-tolerant endpoints, DO NOT
        // flip the auth state to logged-out on a single refresh miss. The
        // driver is likely between towers or the OS just resumed the JS
        // runtime. The next call from the foreground will refresh cleanly.
        // Logging out mid-delivery is far worse than a dropped ping.
        if (!isBackgroundTolerant(url)) {
          if (typeof onUnauthorized === 'function') onUnauthorized();
        }
        return Promise.reject(error);
      }

      // Refresh succeeded — replay the original request with fresh cookie
      // AND fresh bearer (the request interceptor will re-attach the
      // rotated session_token automatically on retry).
      original.withCredentials = true;
      if (original.headers) {
        delete original.headers.Authorization;
        delete original.headers.authorization;
      }
      return axios(original);
    },
  );
  axios.__ashMerchAuthInterceptor = id;
  return () => {
    axios.interceptors.response.eject(id);
    axios.__ashMerchAuthInterceptor = null;
    if (axios.__ashMerchAuthRequestInterceptor != null) {
      axios.interceptors.request.eject(axios.__ashMerchAuthRequestInterceptor);
      axios.__ashMerchAuthRequestInterceptor = null;
    }
  };
}
